'use strict'

import '../../../assets/css/style.css'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Link } from '@mui/material'
import { viewId, titleName } from '../../../_constants/Code'
import Forbidden from '../../../_components/Forbidden'
import { useModal, useModalExtra } from '../../../_components/Modal'
import { useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import { useToast } from '../../../_components/Toast'
import { errorLog } from '../../../common/logger/Logger'
import { getMessage } from '../../../common/utils/MessageUtils'
import {
  fetchConversionTableList,
  fetchClassificationList,
  fetchChrofyClassificationListFromAppMaster,
  mapChrofyClassificationAppMasterRowsToDropdownItems,
  isChrofyClassificationConversionTarget,
  isDataClassificationConversionTarget,
  fetchChrofyClassificationItemValuesForValidation,
  fetchConversionHistory,
  fetchClassificationItemValues,
  getConversionValues,
  checkConversionTable,
  updateConversionTable,
  updateConversionTableMeta,
  uploadConversionValuesPayloadToS3,
  deleteConversionTableApi
} from './extra/services'
import {
  parseCSV,
  parseExcel,
  getExcelSheetNames,
  validateDuplicateBefore,
  validateAfterInClassification,
  validateFirstRowIsHeaderCSV,
  validateFirstRowIsHeaderExcel,
  IMPORT_HEADER_ERROR,
  downloadCSV,
  downloadExcel,
  apiConversionListToItems,
  itemsToApiConversionList,
  getTableKey,
  getTableName,
  getTableMemo,
  getTableTarget,
  getTableDisplayOrder
} from './extra/helpers'
import './extra/types'
import ConversionTableSidebar from './components/ConversionTableSidebar'
import ConversionTableToolbar from './components/ConversionTableToolbar'
import ConversionItemTable from './components/ConversionItemTable'
import { ShowDeleteError } from './components/ShowErrors'
import { useModifyClassList as modifyClassList } from '../../../common/utils/CSSUtils'
import ConversionTableModalContent from './components/ConversionTableModal'
import HistoryModalContent from './components/HistoryModal'
import { SpinnerButton } from '../../common/CustomButton'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { ConversionTableProviderStore, useConversionTableDispatch, useConversionTableSelector } from './extra/store'

/** 薄いグレー枠でセクションを囲む（人数カウント設定画面と同様） */
const BorderedSection = ({ children, className = '' }) => (
  <div
    className={`border border-gray-300 rounded-xl mt-5 ${className}`}
    style={{ display: 'flex', flexDirection: 'column', padding: 12, height: '100%', boxSizing: 'border-box' }}
  >
    {children}
  </div>
)

BorderedSection.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string
}

/** テンプレートダウンロード用サンプルデータ（1行目ヘッダー＋2・3行目サンプル） */
const TEMPLATE_SAMPLE_ITEMS = [
  { before: 'サンプル1', after: '値1' },
  { before: 'サンプル2', after: '値2' }
]

const TEMPLATE_DOWNLOAD_FILENAME = '変換表インポート用テンプレート'

/** 変換マスター管理画面エントリ（権限チェック） */
const ConversionTableInfo = () => {
  const state = useSelector((s) => s)
  const navigate = useNavigate()
  const showToast = useToast()

  const hasAuth =
    state.authority?.Master_AuthInfo?.Availability &&
    state.authority?.Master_AuthInfo?.Auth_Conf?.DataMapping?.ConversionTable_Set

  if (!hasAuth) {
    return <Forbidden />
  }

  return (
    <ConversionTableProviderStore>
      <ConversionTableInfoContent
        pageTitle="変換表設定"
        corporationNumber={state.company?.corporationNumber}
        navigate={navigate}
        showToast={showToast}
      />
    </ConversionTableProviderStore>
  )
}

/** 変換マスター管理画面コンテンツ */
const ConversionTableInfoContent = ({ pageTitle, corporationNumber, navigate, showToast }) => {
  const dispatch = useConversionTableDispatch()
  const state = useConversionTableSelector((s) => s)
  const {
    tableList,
    classifications,
    selectedTable,
    conversionItems,
    previewItems,
    validationErrors,
    validationErrorByTableKey,
    importFileName,
    isLoading,
    initError,
    deleteError,
    importError
  } = state

  const showModal = useModal()
  const { _closeUpModal } = useModalExtra()
  const { promisify, addToClassList } = modifyClassList('.modal-window', ['w-[900px]'])
  const openConfirmationModal = useConfirmationModal()

  /**
   * 変換項目のバリデーション結果を画面表示用と左タブのエラー表示用に反映（区分マスター設定の TypeFilter と同様の赤背景）
   *
   * @param {import('./extra/types').ValidationError[]} errors
   * @param {string | null} tableKey - Null のときは左タブ用フラグを更新しない
   */
  const setValidationErrorsForTable = useCallback(
    (errors, tableKey) => {
      dispatch('SET_VALIDATION_ERRORS', errors || [])
      if (tableKey) {
        dispatch('SET_TABLE_VALIDATION_ERROR_FLAG', {
          tableKey,
          hasError: Array.isArray(errors) && errors.length > 0
        })
      }
    },
    [dispatch]
  )

  const [loading, setLoading] = useState(true)
  /** 右ペインで選択中の変換先区分（「なし」は空文字）。登録・メタ更新時に使用 */
  const [pendingConversionTarget, setPendingConversionTarget] = useState('')
  /** 登録確認モーダル表示前に実行した更新判定IFのレスポンス（子画面の実行時に変換表更新IFへ渡す） */
  const [checkConversionTablePayload, setCheckConversionTablePayload] = useState(null)
  /** テンプレートダウンロードドロップダウン開閉 */
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false)
  const templateDropdownRef = useRef(null)
  /** インポート中の Excel ファイルの ArrayBuffer を保持する。 シート選択変更時に同一ファイルを別シートで再パースするために使用する（CSV インポート時・表切替時は null）。 */
  const importBufferRef = useRef(null)

  /** 初期データ取得（変換表一覧、区分一覧、変更履歴） */
  const init = useCallback(async () => {
    document.title = pageTitle + titleName.common
    dispatch('RESET_STATE')

    if (!corporationNumber) {
      setLoading(false)
      return
    }

    dispatch('SET_IS_LOADING', true)
    dispatch('SET_INIT_ERROR', null)
    dispatch('SET_DELETE_ERROR', null) // 削除エラーをクリア
    // 初期表示時は何も選択していない状態にする
    dispatch('SET_SELECTED_TABLE', null)
    dispatch('SET_CONVERSION_ITEMS', [])
    dispatch('RESET_PREVIEW')
    importBufferRef.current = null

    try {
      const [tables, classificationsRes, chrofyRows] = await Promise.all([
        fetchConversionTableList(corporationNumber),
        fetchClassificationList(corporationNumber),
        fetchChrofyClassificationListFromAppMaster(corporationNumber).catch(() => [])
      ])
      const chrofyForDropdown = mapChrofyClassificationAppMasterRowsToDropdownItems(chrofyRows)
      dispatch('SET_TABLE_LIST', tables || [])
      dispatch('SET_CLASSIFICATIONS', [...(classificationsRes || []), ...chrofyForDropdown])
    } catch (err) {
      // エラー時は既存データを保持（ネットワークエラー時に画面が真っ白にならないように）
      // 初回ロード時のみ空配列をセット
      if (!tableList || tableList.length === 0) {
        dispatch('SET_TABLE_LIST', [])
      }
      if (!classifications || classifications.length === 0) {
        dispatch('SET_CLASSIFICATIONS', [])
      }

      const errorMessage = getMessage('E011012')
      dispatch('SET_INIT_ERROR', errorMessage)

      errorLog('E000001', null, viewId.conversionTableInfo, 'init', {
        error: err.message,
        stack: err.stack,
        response: err?.response?.data,
        status: err?.response?.status,
        corporationNumber
      })
    } finally {
      dispatch('SET_IS_LOADING', false)
      setLoading(false)
    }
  }, [corporationNumber, pageTitle, tableList, classifications, dispatch])

  /**
   * 選択された変換表の変換項目を取得
   *
   * @param {Object} table - 選択された変換表
   */
  const fetchTableData = useCallback(
    async (table) => {
      if (!table || !corporationNumber) return []
      const key = getTableKey(table)
      try {
        const res = await getConversionValues(corporationNumber, key)
        const list = res?.conversion_value_list ?? res?.body?.conversion_value_list ?? []
        return apiConversionListToItems(list)
      } catch (err) {
        const msg = err?.response?.data?.message ?? getMessage('E011012')
        showToast(msg)
        errorLog('E000001', null, viewId.conversionTableInfo, 'fetchTableData', {
          error: err.message,
          stack: err.stack,
          response: err?.response?.data,
          status: err?.response?.status,
          tableKey: key,
          corporationNumber
        })
        return []
      }
    },
    [corporationNumber, showToast]
  )

  /**
   * 変換表一覧を再取得して状態を更新
   *
   * @returns {Promise<Array>} 更新された変換表一覧
   */
  const refreshTableList = useCallback(async () => {
    try {
      const tables = await fetchConversionTableList(corporationNumber)
      dispatch('SET_TABLE_LIST', tables || [])
      return tables
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || '変換表一覧の更新に失敗しました。画面を再読み込みしてください。'
      console.error('変換表一覧の再取得に失敗しました:', err)
      showToast(errorMessage)
      errorLog('E000001', null, viewId.conversionTableInfo, 'refreshTableList', {
        error: err.message,
        stack: err.stack,
        response: err?.response?.data,
        status: err?.response?.status,
        corporationNumber
      })
      throw err
    }
  }, [corporationNumber, dispatch, showToast])

  /**
   * 次の表示順序を計算
   *
   * @param {Array} tableList - 変換表一覧
   * @returns {number} 次の表示順序
   */
  const getNextDisplayOrder = useCallback((tableList) => {
    if (!tableList || !Array.isArray(tableList) || tableList.length === 0) return 1

    // 数値のみを抽出してフィルタリング
    const orders = tableList
      .map((t) => {
        const order = getTableDisplayOrder(t)
        return typeof order === 'number' && !isNaN(order) ? order : 0
      })
      .filter((order) => order >= 0)

    // 配列が空の場合は1を返す
    if (orders.length === 0) return 1

    const maxOrder = Math.max(...orders, 0)

    // maxOrderが有効な数値であることを確認
    return typeof maxOrder === 'number' && !isNaN(maxOrder) && isFinite(maxOrder) ? maxOrder + 1 : 1
  }, [])

  /**
   * 編集対象の変換表が現在選択中かどうかを判定
   *
   * @param {Object} table - 編集対象の変換表
   * @param {Object} selectedTable - 現在選択中の変換表
   * @returns {boolean} 選択中の場合true
   */
  const isSelectedTable = useCallback((table, selectedTable) => {
    if (!table || !selectedTable) return false
    return getTableKey(table) === getTableKey(selectedTable)
  }, [])

  /**
   * 変換先区分名を取得
   *
   * @param {string} key - 区分のsearch_key
   * @returns {string} 区分名
   */
  const getClassificationName = useCallback(
    (key) => {
      if (!key || typeof key !== 'string') return ''
      if (!Array.isArray(classifications)) return ''
      const c = classifications.find((x) => x && x.search_key === key)
      return c?.data_classification_name ?? ''
    },
    [classifications]
  )

  /**
   * インポートされた変換項目をバリデーション
   *
   * @param {import('./extra/types').ConversionItem[]} items - 変換項目
   * @param {string} conversionTarget - 変換先区分のsearch_key
   * @returns {Promise<import('./extra/types').ValidationError[]>}
   */
  const validateImportedItems = useCallback(
    async (items, conversionTarget) => {
      const dupErrors = validateDuplicateBefore(items)
      let catErrors = []

      if (conversionTarget) {
        try {
          if (isChrofyClassificationConversionTarget(conversionTarget)) {
            const validValues = await fetchChrofyClassificationItemValuesForValidation(
              corporationNumber,
              conversionTarget
            )
            catErrors = validateAfterInClassification(items, validValues)
          } else if (isDataClassificationConversionTarget(conversionTarget)) {
            const standardFormatColumnName = getClassificationName(conversionTarget)

            if (standardFormatColumnName) {
              const validValues = await fetchClassificationItemValues(
                corporationNumber,
                conversionTarget,
                standardFormatColumnName
              )
              catErrors = validateAfterInClassification(items, validValues)
            }
          }
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message || '区分値の検証に失敗しました。変換後の値の検証はスキップされます。'
          console.error('区分値の検証に失敗しました:', err)
          showToast(errorMessage)
          errorLog('E000001', null, viewId.conversionTableInfo, 'validateImportedItems', {
            error: err.message,
            stack: err.stack,
            response: err?.response?.data,
            status: err?.response?.status,
            conversionTarget,
            corporationNumber
          })
        }
      }

      return [...dupErrors, ...catErrors]
    },
    [corporationNumber, getClassificationName, showToast]
  )

  /**
   * 右ペインの変換先区分プルダウン変更時：未保存の区分で再バリデーション
   *
   * @param {string} newValue - 変換先区分の search_key（なしは空文字）
   */
  const handleConversionTargetChange = useCallback(
    async (newValue) => {
      setPendingConversionTarget(newValue)
      if (!selectedTable) return
      const tableKey = getTableKey(selectedTable)
      const itemsToValidate = previewItems ?? conversionItems
      if (!itemsToValidate?.length) {
        setValidationErrorsForTable([], tableKey)
        return
      }
      const errors = await validateImportedItems(itemsToValidate, newValue)
      setValidationErrorsForTable(errors, tableKey)
    },
    [selectedTable, previewItems, conversionItems, validateImportedItems, setValidationErrorsForTable]
  )

  /**
   * 変換表選択時のハンドラー
   *
   * @param {Object} table - 選択された変換表
   */
  const handleSelectTable = useCallback(
    async (table) => {
      const applySelection = async () => {
        const previousTableKey = selectedTable ? getTableKey(selectedTable) : null
        const nextTableKey = table ? getTableKey(table) : null
        const hadPreviewToDiscard = previewItems && previewItems.length > 0

        const metaForPrev =
          selectedTable && (tableList.find((t) => getTableKey(t) === getTableKey(selectedTable)) ?? selectedTable)
        const savedConversionTargetForPrev = metaForPrev
          ? (metaForPrev.ConversionTarget ?? metaForPrev.conversion_target ?? '')
          : ''
        const hadUnsavedConversionTargetChange = savedConversionTargetForPrev !== pendingConversionTarget

        // プレビュー破棄、または未保存の変換先区分変更を破棄して別表へ: 登録済み項目 × 保存済み変換先区分で再検証し、離脱する表の左タブ赤を実データに合わせる
        if (
          previousTableKey &&
          previousTableKey !== nextTableKey &&
          (hadPreviewToDiscard || hadUnsavedConversionTargetChange)
        ) {
          const itemsToValidate = conversionItems ?? []
          const errors = await validateImportedItems(itemsToValidate, savedConversionTargetForPrev)
          dispatch('SET_TABLE_VALIDATION_ERROR_FLAG', {
            tableKey: previousTableKey,
            hasError: errors.length > 0
          })
        }

        dispatch('RESET_PREVIEW')
        importBufferRef.current = null // 別表へ切り替えるため保持中のインポートバッファを破棄

        dispatch('SET_DELETE_ERROR', null) // 削除エラーをクリア
        dispatch('SET_SELECTED_TABLE', table)
        if (!table) {
          dispatch('SET_CONVERSION_ITEMS', [])
          dispatch('SET_VALIDATION_ERRORS', []) // バリデーションエラーもクリア
          dispatch('CLEAR_TABLE_VALIDATION_ERROR_FLAGS')
          setPendingConversionTarget('')
          return
        }
        const ct = table.ConversionTarget ?? table.conversion_target ?? ''
        setPendingConversionTarget(ct)
        dispatch('SET_CONVERSION_ITEMS', []) // API実行前に明示的にクリア
        dispatch('SET_IS_LOADING', true)
        try {
          const items = await fetchTableData(table)
          dispatch('SET_CONVERSION_ITEMS', items)

          // 変換表選択時：重複は常にチェック。区分値チェックは「なし」以外のみ
          if (items && items.length > 0) {
            const errors = await validateImportedItems(items, ct)
            setValidationErrorsForTable(errors, getTableKey(table))
          } else {
            setValidationErrorsForTable([], getTableKey(table))
          }
        } finally {
          dispatch('SET_IS_LOADING', false)
        }
      }

      const previousTableKey = selectedTable ? getTableKey(selectedTable) : null
      const nextTableKey = table ? getTableKey(table) : null
      const isSwitchingToDifferentTable = Boolean(previousTableKey && nextTableKey && previousTableKey !== nextTableKey)
      const metaForSelected =
        selectedTable && (tableList.find((t) => getTableKey(t) === getTableKey(selectedTable)) ?? selectedTable)
      const savedConversionTarget = metaForSelected
        ? (metaForSelected.ConversionTarget ?? metaForSelected.conversion_target ?? '')
        : ''
      const hasUnsavedConversionTargetChange =
        isSwitchingToDifferentTable && savedConversionTarget !== pendingConversionTarget

      // プレビュー破棄、または未保存の変換先区分変更がある状態で別表へ切り替える場合は同じ確認モーダル
      const hadPreviewToDiscard = previewItems && previewItems.length > 0
      if (hadPreviewToDiscard || hasUnsavedConversionTargetChange) {
        await openConfirmationModal({
          title: '変更内容が破棄されます',
          description: 'よろしいですか？',
          confirmName: '切り替える',
          onClick: applySelection
        })
        return
      }

      await applySelection()
    },
    [
      previewItems,
      pendingConversionTarget,
      selectedTable,
      tableList,
      conversionItems,
      fetchTableData,
      validateImportedItems,
      dispatch,
      openConfirmationModal,
      setValidationErrorsForTable,
      setPendingConversionTarget
    ]
  )

  /**
   * 新規変換表の保存
   *
   * @param {Object} form - フォームデータ（name, conversion_target, memo）
   */
  const handleSaveNewTable = useCallback(
    async (form) => {
      if (!corporationNumber) return

      const requestPayload = {
        key: '', // 新規登録の場合は空文字
        name: form.name,
        conversion_target: '', // 変換先区分は右ペインで設定（新規は「なし」）
        memo: form.memo || '',
        display_order: getNextDisplayOrder(tableList)
      }

      dispatch('SET_IS_API_LOADING', true)
      try {
        await updateConversionTableMeta(corporationNumber, requestPayload)
        showToast('変換表を登録しました。')

        // 変換表一覧を再取得
        const tables = await refreshTableList()

        // 新規作成した変換表を選択（名前で検索）
        const newTable = tables.find((t) => t.ConversionTableName === form.name)
        if (newTable) {
          dispatch('SET_SELECTED_TABLE', newTable)
          dispatch('SET_CONVERSION_ITEMS', [])
          setPendingConversionTarget('')
        }
      } catch (err) {
        console.error(err)
        const msg = err?.response?.data?.message ?? getMessage('E011012')
        showToast(msg)
        errorLog('E000001', null, viewId.conversionTableInfo, 'handleSaveNewTable', {
          error: err.message,
          stack: err.stack,
          response: err?.response?.data,
          status: err?.response?.status,
          formData: { name: form.name },
          corporationNumber
        })
      } finally {
        dispatch('SET_IS_API_LOADING', false)
      }
    },
    [corporationNumber, tableList, getNextDisplayOrder, refreshTableList, showToast, dispatch]
  )

  /** 新規変換表作成モーダルを開く */
  const handleNewTable = useCallback(() => {
    dispatch('SET_DELETE_ERROR', null) // 削除エラーをクリア
    showModal({
      title: '変換表設定',
      body: (
        <ConversionTableModalContent
          key={`new-table-${Date.now()}`}
          mode="new"
          initialMeta={null}
          tableList={tableList}
          onClose={_closeUpModal}
          onSave={async (form) => {
            await handleSaveNewTable(form)
            _closeUpModal()
          }}
        />
      ),
      isFooter: false,
      isCancel: false,
      isWide: false
    })
  }, [tableList, showModal, _closeUpModal, handleSaveNewTable, dispatch])

  /** 変更履歴モーダルを開く */
  const handleShowHistory = useCallback(async () => {
    dispatch('SET_IS_API_LOADING', true)
    try {
      const historyRes = await fetchConversionHistory(corporationNumber)

      showModal({
        title: '変更履歴',
        body: <HistoryModalContent key={`history-${Date.now()}`} historyList={historyRes || []} />,
        isWide: true,
        modalClass: 'is-w1456',
        isCancel: true,
        cancelName: 'キャンセル',
        isFooter: true,
        footerClass: 'mt-5'
      })
    } catch (err) {
      const errorMessage = err?.response?.data?.message || '変更履歴の取得に失敗しました。'
      showToast(errorMessage)
      errorLog('E000001', null, viewId.conversionTableInfo, 'handleShowHistory', {
        error: err.message,
        stack: err.stack,
        response: err?.response?.data,
        status: err?.response?.status,
        corporationNumber
      })
    } finally {
      dispatch('SET_IS_API_LOADING', false)
    }
  }, [corporationNumber, showModal, showToast, dispatch])

  /** テンプレートを指定形式でダウンロード（CSV または Excel） */
  const handleTemplateDownload = useCallback(
    (format) => {
      if (format === 'csv') {
        downloadCSV(TEMPLATE_SAMPLE_ITEMS, TEMPLATE_DOWNLOAD_FILENAME)
      } else if (format === 'excel') {
        downloadExcel(TEMPLATE_SAMPLE_ITEMS, TEMPLATE_DOWNLOAD_FILENAME)
      }
      setIsTemplateDropdownOpen(false)
      showToast('テンプレートをダウンロードしました。')
    },
    [showToast]
  )

  /**
   * 現在表示中のデータ（プレビューまたは登録済み）に対して再バリデーションを実行
   *
   * @param {string} conversionTarget - 変換先区分のsearch_key
   * @returns {Promise<import('./extra/types').ValidationError[]>} バリデーションエラーの配列
   */
  const revalidateCurrentDisplayItems = useCallback(
    async (conversionTarget) => {
      // プレビュー中のデータを優先、なければ登録済みデータ
      const itemsToValidate = previewItems || conversionItems

      if (!itemsToValidate || itemsToValidate.length === 0) {
        const tableKey = selectedTable ? getTableKey(selectedTable) : null
        setValidationErrorsForTable([], tableKey)
        return []
      }

      const errors = await validateImportedItems(itemsToValidate, conversionTarget)

      if (errors && errors.length > 0) {
        const dataType = previewItems ? 'インポートデータ' : '既存の変換項目'
        showToast(`変換先区分の変更により、${dataType}にエラーが検出されました。`)
      }

      const tableKey = selectedTable ? getTableKey(selectedTable) : null
      setValidationErrorsForTable(errors, tableKey)

      return errors
    },
    [previewItems, conversionItems, validateImportedItems, showToast, selectedTable, setValidationErrorsForTable]
  )

  /**
   * 編集後の変換表をバリデーションしてトースト表示
   *
   * @param {Object} updatedTable - 更新された変換表
   * @param {boolean} isEditingSelected - 選択中の変換表を編集したかどうか
   * @returns {Promise<void>}
   */
  const validateAndNotifyTableUpdate = useCallback(
    async (updatedTable, isEditingSelected) => {
      const newConversionTarget = updatedTable.ConversionTarget ?? updatedTable.conversion_target ?? ''
      const tableName = getTableName(updatedTable)

      try {
        let errors = []

        if (isEditingSelected) {
          // 選択中の変換表を編集した場合、現在表示中のデータを再バリデーション
          dispatch('SET_SELECTED_TABLE', updatedTable)
          setPendingConversionTarget(newConversionTarget)
          errors = await revalidateCurrentDisplayItems(newConversionTarget)
        } else {
          // 選択されていない変換表を編集した場合、一時的にデータを取得してバリデーション
          const editedTableItems = await fetchTableData(updatedTable)

          if (editedTableItems && editedTableItems.length > 0) {
            errors = await validateImportedItems(editedTableItems, newConversionTarget)
          }
          dispatch('SET_TABLE_VALIDATION_ERROR_FLAG', {
            tableKey: getTableKey(updatedTable),
            hasError: Array.isArray(errors) && errors.length > 0
          })
        }

        // トースト表示
        if (errors && errors.length > 0) {
          showToast(`変換先区分の変更により、「${tableName}」の変換項目にエラーが検出されました。`)
        } else {
          showToast('変換表を更新しました。')
        }
      } catch (validationErr) {
        console.error('編集した変換表のバリデーションに失敗しました:', validationErr)
        errorLog('E000001', null, viewId.conversionTableInfo, 'validateAndNotifyTableUpdate', {
          error: validationErr.message,
          stack: validationErr.stack,
          tableName,
          newConversionTarget,
          isEditingSelected
        })
        // バリデーションエラーは致命的ではないため、処理は続行し、成功トーストを表示
        showToast('変換表を更新しました。')
      }
    },
    [fetchTableData, validateImportedItems, revalidateCurrentDisplayItems, showToast, dispatch]
  )

  /**
   * 既存変換表の編集保存
   *
   * @param {Object} form - フォームデータ（name, memo）
   * @param {Object} meta - 編集対象のメタデータ
   */
  const handleSaveEditTable = useCallback(
    async (form, meta) => {
      if (!corporationNumber) return

      const key = meta?.key
      const existing = tableList.find((t) => getTableKey(t) === key)
      if (!existing) return

      const editingSelected = isSelectedTable(existing, selectedTable)
      const conversionTargetForSave = editingSelected
        ? pendingConversionTarget
        : (existing.ConversionTarget ?? existing.conversion_target ?? '')

      const requestPayload = {
        key: key,
        name: form.name,
        conversion_target: conversionTargetForSave,
        memo: form.memo ?? '',
        display_order: getTableDisplayOrder(existing) ?? 1
      }

      dispatch('SET_IS_API_LOADING', true)
      try {
        await updateConversionTableMeta(corporationNumber, requestPayload)

        // 変換表一覧を再取得
        const tables = await refreshTableList()

        // 編集した変換表を取得
        const updatedTable = tables.find((t) => getTableKey(t) === key)
        if (updatedTable) {
          const isEditingSelected = isSelectedTable(updatedTable, selectedTable)
          await validateAndNotifyTableUpdate(updatedTable, isEditingSelected)
        } else {
          // 変換表が見つからない場合も成功トーストを表示
          showToast('変換表を更新しました。')
        }
      } catch (err) {
        console.error(err)
        const msg = err?.response?.data?.message ?? getMessage('E011012')
        showToast(msg)
        errorLog('E000001', null, viewId.conversionTableInfo, 'handleSaveEditTable', {
          error: err.message,
          stack: err.stack,
          response: err?.response?.data,
          status: err?.response?.status,
          formData: { name: form.name, conversion_target: conversionTargetForSave },
          metaKey: meta?.key,
          corporationNumber
        })
      } finally {
        dispatch('SET_IS_API_LOADING', false)
      }
    },
    [
      corporationNumber,
      tableList,
      selectedTable,
      pendingConversionTarget,
      isSelectedTable,
      refreshTableList,
      validateAndNotifyTableUpdate,
      showToast,
      dispatch
    ]
  )

  /**
   * 変換表編集モーダルを開く
   *
   * @param {Object} table - 編集対象の変換表
   */
  const handleEditTable = useCallback(
    (table) => {
      dispatch('SET_DELETE_ERROR', null) // 削除エラーをクリア
      const meta = {
        key: getTableKey(table),
        name: getTableName(table),
        memo: getTableMemo(table),
        display_order: getTableDisplayOrder(table) ?? 0
      }
      showModal({
        title: '変換表設定',
        body: (
          <ConversionTableModalContent
            mode="edit"
            initialMeta={meta}
            tableList={tableList}
            onClose={_closeUpModal}
            onSave={async (form) => {
              await handleSaveEditTable(form, meta)
              _closeUpModal()
            }}
          />
        ),
        isFooter: false,
        isCancel: false,
        isWide: false
      })
    },
    [tableList, showModal, _closeUpModal, handleSaveEditTable, dispatch]
  )

  /**
   * ファイル拡張子がExcelかどうかを判定
   *
   * @param {string} fileName - ファイル名
   * @returns {boolean} Excelファイルの場合true
   */
  const isExcelFile = useCallback((fileName) => {
    return fileName.endsWith('.xlsx') || fileName.endsWith('.xls')
  }, [])

  /**
   * ファイルインポートエラーメッセージを取得
   *
   * @param {Error} err - エラーオブジェクト
   * @param {string} fileName - ファイル名
   * @returns {string} エラーメッセージ
   */
  const getImportErrorMessage = useCallback(
    (err, fileName) => {
      if (err.message === 'UNSUPPORTED_FORMAT') {
        return 'サポートされていないファイル形式です。'
      }
      if (err.message === IMPORT_HEADER_ERROR) {
        return '1行目に「変換前」「変換後」のヘッダーが必要です。テンプレートをダウンロードして形式を確認し、ファイルを修正してから再度インポートしてください。'
      }
      return isExcelFile(fileName) ? 'Excelファイルの読み込みに失敗しました。' : 'ファイルの読み込みに失敗しました。'
    },
    [isExcelFile]
  )

  /**
   * パース済みの変換項目をバリデーションしてプレビューへ反映する（インポート・シート変更で共用）
   *
   * @param {import('./extra/types').ConversionItem[]} items - パース済み変換項目
   * @param {string} tableKey - 対象変換表キー（左タブ赤フラグ更新用）
   * @param {string} conversionTarget - 変換先区分の search_key
   */
  const applyPreviewItems = useCallback(
    async (items, tableKey, conversionTarget) => {
      const errors = await validateImportedItems(items, conversionTarget)
      setValidationErrorsForTable(errors, tableKey)
      dispatch('SET_PREVIEW_ITEMS', items)
      dispatch('SET_IMPORT_ERROR', null)
    },
    [validateImportedItems, setValidationErrorsForTable, dispatch]
  )

  /**
   * ファイルインポート処理
   *
   * @param {File} file - インポートファイル
   * @param {string} [sheetName] - Excelシート名
   */
  const handleFileImport = useCallback(
    async (file, sheetName) => {
      if (!selectedTable) return

      const conversionTarget = pendingConversionTarget
      const fileName = file.name || ''
      const isExcel = isExcelFile(fileName)
      const isCsv = fileName.endsWith('.csv')

      dispatch('SET_IMPORT_ERROR', null)
      dispatch('SET_IMPORT_FILE_NAME', fileName)

      try {
        let items
        if (isExcel) {
          // 同一ファイルを別シートで再パースできるよう ArrayBuffer を保持する
          const buf = await file.arrayBuffer()
          importBufferRef.current = buf
          const sheets = await getExcelSheetNames(buf)
          dispatch('SET_IMPORT_SHEET_NAMES', sheets)
          const sheetToUse = sheetName || sheets[0] || ''
          if (!sheetToUse) {
            showToast('有効なデータがありません。')
            return
          }
          dispatch('SET_SELECTED_SHEET_NAME', sheetToUse)
          await validateFirstRowIsHeaderExcel(buf, sheetToUse)
          items = await parseExcel(buf, sheetToUse)
        } else if (isCsv) {
          // CSV はシートを持たないため、保持中のバッファ・シート情報をクリアする
          importBufferRef.current = null
          dispatch('SET_IMPORT_SHEET_NAMES', [])
          dispatch('SET_SELECTED_SHEET_NAME', '')
          const text = await file.text()
          validateFirstRowIsHeaderCSV(text)
          items = parseCSV(text)
        } else {
          throw new Error('UNSUPPORTED_FORMAT')
        }

        if (!items || items.length === 0) {
          showToast('有効なデータがありません。')
          return
        }

        const tableKey = getTableKey(selectedTable)
        // プレビューに切り替える直前の状態（インポートで上書きする前）。登録データ由来のエラーがあればプレビュー破棄後も左タブ赤を復元する。
        const baselineHadValidationIssue =
          validationErrorByTableKey[tableKey] === true ||
          (Array.isArray(validationErrors) && validationErrors.length > 0)
        dispatch('SET_PREVIEW_BASELINE_VALIDATION', baselineHadValidationIssue)

        await applyPreviewItems(items, tableKey, conversionTarget)
      } catch (err) {
        console.error(err)
        if (err.message === IMPORT_HEADER_ERROR) {
          dispatch('SET_IMPORT_ERROR', getImportErrorMessage(err, fileName))
          window.scrollTo(0, 0)
          return
        }
        showToast(getImportErrorMessage(err, fileName))
      }
    },
    [
      selectedTable,
      isExcelFile,
      applyPreviewItems,
      getImportErrorMessage,
      showToast,
      dispatch,
      validationErrorByTableKey,
      validationErrors,
      pendingConversionTarget
    ]
  )

  /**
   * 選択中の変換表のメタデータを取得
   *
   * @returns {Object | null} 変換表メタデータ
   */
  const getSelectedTableMeta = useCallback(() => {
    if (!selectedTable) return null

    const key = getTableKey(selectedTable)
    return tableList.find((t) => getTableKey(t) === key) || selectedTable
  }, [selectedTable, tableList])

  /**
   * シート選択変更時のハンドラー（設計書11219 §7.4：選択シートをJSON化してプレビュー・検証へ反映）
   *
   * 保持中の Excel バッファを選択シートで再パースし、プレビュー・プレビューバッジ・バリデーション結果を更新する。
   *
   * @param {string} sheetName - 選択されたシート名
   */
  const handleSheetChange = useCallback(
    async (sheetName) => {
      dispatch('SET_SELECTED_SHEET_NAME', sheetName)

      const buf = importBufferRef.current
      if (!buf || !selectedTable) return

      const tableKey = getTableKey(selectedTable)
      try {
        await validateFirstRowIsHeaderExcel(buf, sheetName)
        const items = await parseExcel(buf, sheetName)

        if (!items || items.length === 0) {
          // 選択シートにデータが無い場合は空プレビュー＋検証クリアで実データに合わせる
          dispatch('SET_PREVIEW_ITEMS', [])
          setValidationErrorsForTable([], tableKey)
          dispatch('SET_IMPORT_ERROR', null)
          showToast('有効なデータがありません。')
          return
        }

        await applyPreviewItems(items, tableKey, pendingConversionTarget)
      } catch (err) {
        console.error(err)
        if (err.message === IMPORT_HEADER_ERROR) {
          dispatch('SET_IMPORT_ERROR', getImportErrorMessage(err, importFileName))
          window.scrollTo(0, 0)
          return
        }
        showToast(getImportErrorMessage(err, importFileName || ''))
      }
    },
    [
      dispatch,
      selectedTable,
      pendingConversionTarget,
      applyPreviewItems,
      setValidationErrorsForTable,
      getImportErrorMessage,
      showToast,
      importFileName
    ]
  )

  const handleExport = useCallback(
    (format) => {
      // フォーマットのバリデーション
      if (!format || (format !== 'csv' && format !== 'excel')) {
        showToast('無効なエクスポート形式です。')
        return
      }

      const selectedMeta = getSelectedTableMeta()
      const isPreviewMode = previewItems !== null
      const displayItems = isPreviewMode ? previewItems : conversionItems
      const hasItems = Array.isArray(displayItems) && displayItems.length > 0

      if (!hasItems) {
        showToast('エクスポートするデータがありません。')
        return
      }

      const name = getTableName(selectedMeta) || 'conversion_table'
      if (format === 'csv') {
        downloadCSV(displayItems, name)
      } else {
        downloadExcel(displayItems, name)
      }
    },
    [getSelectedTableMeta, previewItems, conversionItems, showToast]
  )

  /**
   * 登録前のバリデーション
   *
   * @returns {{ isValid: boolean; selectedMeta: Object | null; displayItems: Array }}
   */
  const validateRegistration = useCallback(() => {
    const selectedMeta = getSelectedTableMeta()

    const isPreviewMode = previewItems !== null
    const displayItems = isPreviewMode ? previewItems : conversionItems
    const hasItems = Array.isArray(displayItems) && displayItems.length > 0
    const hasNoErrors = !Array.isArray(validationErrors) || validationErrors.length === 0

    return {
      isValid: hasItems && hasNoErrors,
      selectedMeta,
      displayItems: displayItems || []
    }
  }, [getSelectedTableMeta, previewItems, conversionItems, validationErrors])

  /**
   * 変換項目更新判定APIを実行してペイロードを準備
   *
   * @param {string} apiKey - 変換表キー
   * @param {Array} displayItems - 表示中の変換項目
   * @returns {Promise<{ requestPayload: Object; dataCreateTaskInfoList: Array }>}
   */
  const prepareRegistrationPayload = useCallback(
    async (apiKey, displayItems) => {
      // IF02076: 変換項目更新判定 - conversion_table_keyのみを送信
      const checkRequestPayload = {
        conversion_table_key: apiKey
      }
      const checkRes = await checkConversionTable(corporationNumber, checkRequestPayload)

      // IF02076のレスポンスからdata_create_task_info_listを取得（必ず返却される）
      const dataCreateTaskInfoList =
        checkRes?.body?.data_create_task_info_list ?? checkRes?.data_create_task_info_list ?? []

      // IF02071: 変換項目更新用のペイロード（仕様に準拠）
      const requestPayload = {
        conversion_table_key: apiKey,
        conversion_table_items: itemsToApiConversionList(displayItems),
        data_create_task_info_list: dataCreateTaskInfoList
      }

      return { requestPayload, dataCreateTaskInfoList }
    },
    [corporationNumber]
  )

  /**
   * 登録確認モーダルの説明文を生成
   *
   * @param {Array} dataCreateTaskInfoList - データ作成タスク情報リスト
   * @returns {JSX.Element}
   */
  const buildModalDescription = useCallback((dataCreateTaskInfoList) => {
    const hasTaskInfo = dataCreateTaskInfoList && dataCreateTaskInfoList.length > 0
    const periodTexts = hasTaskInfo
      ? dataCreateTaskInfoList
          .map((info) => info.period_value)
          .filter(Boolean)
          .join('、')
      : ''

    return (
      <React.Fragment>
        {hasTaskInfo && (
          <div className="pb-5 text-center">
            {periodTexts ? (
              <>
                この変換表は{periodTexts}で使用しています。
                <br />
              </>
            ) : null}
            登録を行うと、データの検証および更新が実行されます。
          </div>
        )}
        <div className="text-center">設定値を登録してダッシュボードに反映します。よろしいですか？</div>
        <div className="pt-5 text-center">
          <p>※ダッシュボードへの反映には5分～10分程度かかります</p>
          <p>マスター管理画面のステータスから反映状況を確認してください</p>
        </div>
      </React.Fragment>
    )
  }, [])

  /** 変換表登録の実行（payload を渡すとそれを使用。未渡しの場合は state の checkConversionTablePayload を使用） */
  const handleRegisterConfirm = useCallback(
    async (payloadOverride) => {
      if (!corporationNumber) return
      const payload = payloadOverride ?? checkConversionTablePayload
      if (!payload) {
        showToast('エラーがあるため登録できません。')
        return
      }

      dispatch('SET_IS_API_LOADING', true)
      try {
        const meta = getSelectedTableMeta()
        if (!meta) {
          showToast('エラーがあるため登録できません。')
          return
        }
        const tableKey = getTableKey(meta)
        const if02082Payload = {
          key: tableKey,
          name: getTableName(meta),
          conversion_target: pendingConversionTarget,
          memo: getTableMemo(meta),
          display_order: getTableDisplayOrder(meta) ?? 1
        }
        const savedName = getTableName(meta)
        const savedConversionTarget = getTableTarget(meta)
        const savedMemo = getTableMemo(meta)
        const savedDisplayOrder = getTableDisplayOrder(meta) ?? 1
        const isMetaUnchanged =
          savedName === if02082Payload.name &&
          savedConversionTarget === if02082Payload.conversion_target &&
          savedMemo === if02082Payload.memo &&
          Number(savedDisplayOrder) === Number(if02082Payload.display_order)

        if (!isMetaUnchanged) {
          await updateConversionTableMeta(corporationNumber, if02082Payload)
        }
        // 変換項目本体（最大10万件）は API Gateway のボディ上限を超えるため、
        // Amplify Storage.put で共通バケットへ直接アップロードし、登録IFにはS3キーのみを送信する。
        const { payload_s3_key, user_id_sub } = await uploadConversionValuesPayloadToS3(payload)
        await updateConversionTable(corporationNumber, {
          conversion_table_key: payload.conversion_table_key,
          payload_s3_key,
          user_id_sub,
          data_create_task_info_list: payload.data_create_task_info_list
        })
        setCheckConversionTablePayload(null)
        navigate('/master/masterList', { state: { message: 'データ反映を開始しました。' } })
      } catch (err) {
        console.error(err)
        const msg = err?.response?.data?.message ?? getMessage('E011012')
        const status = err?.response?.status
        showToast(status === 400 ? err?.response?.data?.message || '登録に失敗しました。' : msg)
        errorLog('E000001', null, viewId.conversionTableInfo, 'handleRegisterConfirm', {
          error: err.message,
          stack: err.stack,
          response: err?.response?.data,
          status: err?.response?.status,
          payloadExists: !!payload,
          corporationNumber
        })
      } finally {
        dispatch('SET_IS_API_LOADING', false)
      }
    },
    [
      corporationNumber,
      checkConversionTablePayload,
      getSelectedTableMeta,
      pendingConversionTarget,
      navigate,
      showToast,
      dispatch
    ]
  )

  /** 登録確認モーダルを開く */
  const handleOpenConfirmRegisterModal = useCallback(async () => {
    // バリデーション
    const { isValid, selectedMeta, displayItems } = validateRegistration()

    if (!isValid) {
      showToast('エラーがあるため登録できません。')
      return
    }

    if (!selectedMeta || !corporationNumber) return

    const key = getTableKey(selectedMeta)
    const apiKey = key?.startsWith('temp_') ? '' : key

    dispatch('SET_IS_API_LOADING', true)
    try {
      // 変換項目更新判定APIを実行してペイロードを準備
      const { requestPayload, dataCreateTaskInfoList } = await prepareRegistrationPayload(apiKey, displayItems)

      // IF02071用のペイロードをStateに保存
      setCheckConversionTablePayload(requestPayload)

      // モーダルの説明文を生成
      const modalDescription = buildModalDescription(dataCreateTaskInfoList)

      // 確認モーダルを表示（React 描画後に幅クラスを付け直す）
      await promisify((res) => {
        showModal({
          title: 'マスター設定登録の確認',
          onClose: res,
          isWide: false,
          description: modalDescription,
          button: [
            {
              name: '実行',
              className: 'footer-btn button_base',
              click: () => handleRegisterConfirm(requestPayload)
            }
          ]
        })
        setTimeout(() => addToClassList(), 0)
      })
    } catch (err) {
      console.error(err)
      if (err?.response?.status === 400) {
        showToast('登録できません。')
      } else {
        const msg = err?.response?.data?.message ?? getMessage('E011012')
        showToast(msg)
      }
      errorLog('E000001', null, viewId.conversionTableInfo, 'handleOpenConfirmRegisterModal', {
        error: err.message,
        stack: err.stack,
        response: err?.response?.data,
        status: err?.response?.status,
        selectedMeta: selectedMeta ? { key: getTableKey(selectedMeta), name: getTableName(selectedMeta) } : null,
        corporationNumber
      })
    } finally {
      dispatch('SET_IS_API_LOADING', false)
    }
  }, [
    validateRegistration,
    corporationNumber,
    prepareRegistrationPayload,
    buildModalDescription,
    handleRegisterConfirm,
    promisify,
    addToClassList,
    showModal,
    showToast,
    dispatch
  ])

  /**
   * 削除エラーメッセージを取得
   *
   * @param {Error} err - エラーオブジェクト
   * @returns {string} エラーメッセージ
   */
  const getDeleteErrorMessage = useCallback((err) => {
    const status = err?.response?.status
    const apiMessage = err?.response?.data?.message

    const defaultMessages = {
      409: 'この変換表は使用中のため削除できません。',
      400: '削除処理でエラーが発生しました。',
      default: getMessage('E011012')
    }

    const defaultMessage = defaultMessages[status] || defaultMessages.default
    return apiMessage || defaultMessage
  }, [])

  /**
   * 変換表削除実行ハンドラー
   *
   * @param {Object} table - 削除対象の変換表
   */
  const handleDeleteTableConfirm = useCallback(
    async (table) => {
      if (!table || !corporationNumber) return

      const conversionTableKey = getTableKey(table)

      if (!conversionTableKey) {
        showToast('変換表キーが取得できませんでした。')
        return
      }

      dispatch('SET_IS_API_LOADING', true)

      try {
        // IF02081: 変換表削除APIを呼び出し
        await deleteConversionTableApi(corporationNumber, conversionTableKey)

        // 削除エラーをクリア
        dispatch('SET_DELETE_ERROR', null)

        // 成功トースト表示
        showToast('変換表を削除しました。')

        dispatch('SET_TABLE_VALIDATION_ERROR_FLAG', { tableKey: conversionTableKey, hasError: false })

        // 変換表一覧を再取得して更新
        const updatedList = await refreshTableList()

        // 削除した変換表が選択中だった場合、選択を解除して初期状態に戻す
        if (isSelectedTable(table, selectedTable)) {
          dispatch('SET_SELECTED_TABLE', null)
          dispatch('SET_CONVERSION_ITEMS', [])
          dispatch('SET_PREVIEW_ITEMS', null)
          importBufferRef.current = null
        }
      } catch (err) {
        console.error('変換表削除エラー:', err)
        errorLog('E000001', null, viewId.conversionTableInfo, 'handleDeleteTableConfirm', {
          error: err.message,
          stack: err.stack,
          response: err?.response?.data,
          status: err?.response?.status,
          tableKey: conversionTableKey,
          tableName: getTableName(table),
          corporationNumber
        })

        // エラーメッセージをstateに保存（画面上部に赤文字で表示）
        const errorMessage = getDeleteErrorMessage(err)
        dispatch('SET_DELETE_ERROR', errorMessage)
        window.scrollTo(0, 0)
      } finally {
        dispatch('SET_IS_API_LOADING', false)
      }
    },
    [corporationNumber, selectedTable, isSelectedTable, refreshTableList, getDeleteErrorMessage, showToast, dispatch]
  )

  /**
   * 変換表削除確認モーダルを開く
   *
   * @param {Object} table - 削除対象の変換表
   */
  const handleDeleteTable = useCallback(
    async (table) => {
      await promisify((res) => {
        showModal({
          title: '変換表削除の確認',
          onClose: res,
          isWide: false,
          description: (
            <React.Fragment>
              <div className="text-center">選択された変換表とその内容を削除します。よろしいでしょうか？</div>
              <div className="pt-5 text-center px-4">
                <div>※削除したデータは元に戻せません。</div>
                <div>※マッピング画面で使用中の場合は削除できません。</div>
              </div>
            </React.Fragment>
          ),
          button: [
            {
              name: '削除',
              className: 'footer-btn button_delete',
              click: () => handleDeleteTableConfirm(table)
            }
          ]
        })
        setTimeout(() => addToClassList(), 0)
      })
    },
    [showModal, promisify, addToClassList, handleDeleteTableConfirm]
  )

  useEffect(() => {
    if (!isTemplateDropdownOpen) return
    const handleClickOutside = (event) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(event.target)) {
        setIsTemplateDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isTemplateDropdownOpen])

  useEffect(() => {
    init()
    window.scrollTo(0, 0)
  }, [])

  // ローディング中は何も表示しない
  if (loading) return null

  const displayItems = previewItems !== null ? previewItems : conversionItems
  const canRegister = !isLoading && displayItems?.length > 0 && (!validationErrors || validationErrors.length === 0)

  return (
    <main className="page-container">
      <section className="page-content">
        <div className="centered-container">
          {/* 上階層に戻る */}
          <div className="bg main">
            <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
              <RouterLink to="/master/masterList">
                <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                  <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10" />
                  <span className="text-h5 font-bold">{titleName.masterList}画面に戻る</span>
                </span>
              </RouterLink>
            </div>
          </div>
          {/* コンテンツヘッダ */}
          <header className="page-header flex justify-center py-1">
            <h1 className="page-header__name text-h1 grey--text py-8" data-testid="page-title">
              変換マスター管理
            </h1>
          </header>
          <div className="page-body">
            <ShowDeleteError errorMessage={deleteError} />
            {importError && (
              <div className="mb-4">
                <span className="red--text">{importError}</span>
              </div>
            )}
            {initError && (
              <div>
                <span style={{ color: 'red' }}>{initError}</span>
              </div>
            )}
            <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-8 px-16 border-blue1 base-table-header">
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                変換マスター管理
              </h2>
              <div className="mt-10 mb-6 flex justify-between items-start">
                <div className="flex flex-col gap-3">
                  <p className="text-body text-h7 grey--text">
                    変換表データの管理を行います。変換表を選択して、変換前と変換後の対応関係を設定してください。
                  </p>
                  <div ref={templateDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      type="button"
                      className="bg-transparent border-0 p-0 m-0 text-left text-body text-h7 text-blue-500 underline cursor-pointer hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1 rounded"
                      onClick={() => setIsTemplateDropdownOpen((prev) => !prev)}
                      data-testid="template-download-button"
                      aria-expanded={isTemplateDropdownOpen}
                      aria-haspopup="menu"
                      aria-controls="template-download-format-menu"
                    >
                      テンプレートをダウンロード
                    </button>
                    {isTemplateDropdownOpen && (
                      <div
                        id="template-download-format-menu"
                        role="menu"
                        aria-label="テンプレートの形式"
                        style={{
                          display: 'block',
                          position: 'absolute',
                          left: 0,
                          top: '100%',
                          background: '#ffffff',
                          minWidth: 180,
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                          borderRadius: 8,
                          zIndex: 100,
                          overflow: 'hidden',
                          border: '1px solid #d8dee9',
                          marginTop: 4
                        }}
                      >
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleTemplateDownload('csv')}
                          data-testid="template-download-csv"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: 13,
                            color: '#1f2937',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#E0E5F2')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          CSV形式
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => handleTemplateDownload('excel')}
                          data-testid="template-download-excel"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            border: 'none',
                            background: 'none',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: 13,
                            color: '#1f2937',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#E0E5F2')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
                        >
                          Excel形式
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                  onClick={handleShowHistory}
                  data-testid="history-button"
                >
                  <AssignmentIcon className="w-5 h-5" />
                  <span>変更履歴</span>
                </button>
              </div>
              <div
                className="container min-w-0"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 7fr)',
                  gap: 12,
                  padding: 12,
                  height: 800,
                  boxSizing: 'border-box'
                }}
              >
                <BorderedSection className="flex flex-col min-h-0 min-w-0 overflow-hidden">
                  <ConversionTableSidebar
                    onSelectTable={handleSelectTable}
                    onNewTable={handleNewTable}
                    onEditTable={handleEditTable}
                    onDeleteTable={handleDeleteTable}
                  />
                </BorderedSection>
                <BorderedSection className="flex flex-col min-h-0 min-w-0 overflow-hidden">
                  <div
                    className="main-content card min-w-0"
                    style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
                  >
                    <ConversionTableToolbar
                      onSheetChange={handleSheetChange}
                      onImportFile={handleFileImport}
                      onExport={handleExport}
                    />
                    <ConversionItemTable
                      pendingConversionTarget={pendingConversionTarget}
                      onConversionTargetChange={handleConversionTargetChange}
                    />
                    <div className="content-footer mt-16 mb-8">
                      <div className="border-t border-blue1" />
                      <div className="footer_button_area mt-8 flex gap-4 justify-end">
                        <Link
                          color="inherit"
                          variant="button"
                          underline="none"
                          component={RouterLink}
                          to="/master/masterList"
                        >
                          <button type="button" className="footer-btn button_back">
                            キャンセル
                          </button>
                        </Link>
                        <SpinnerButton
                          name="登録"
                          className="btn footer-btn button_base"
                          disabled={!canRegister}
                          onClick={handleOpenConfirmRegisterModal}
                          data-testid="register-button"
                        />
                      </div>
                    </div>
                  </div>
                </BorderedSection>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

ConversionTableInfoContent.propTypes = {
  pageTitle: PropTypes.string.isRequired,
  corporationNumber: PropTypes.string,
  navigate: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired
}

export default ConversionTableInfo
